import { getConversationThreadQueryOptions } from "@/api/get-conversation";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import type {
  Conversation,
  ConversationThreadReadConversation,
} from "@rulebase/core/api";
import { Badge } from "@rulebase/core/components";
import { useQuery } from "@tanstack/react-query";
import { CustomerImage } from "./customer-image";
import { JiraIssueView } from "./jira-issue-view";
import {
  LinkedActivityRow,
  LinkedActivityRowList,
} from "./linked-activity-row";
import { SlackMessageView } from "./slack-message-view";

export type LinkedActivityViewProps = {
  conversation: Conversation;
  /**
   * Controlled by the ticket params row, which owns the show/hide toggle so the
   * list needs no header of its own.
   */
  open: boolean;
};

export function LinkedActivityView(props: LinkedActivityViewProps) {
  const { data, isPending, isError } = useQuery(
    getConversationThreadQueryOptions(props.conversation.id),
  );

  if (isPending) return null;

  if (isError && !data) {
    return (
      <p className="py-2 text-neutral-500">
        We couldn&apos;t load linked activity for this ticket.
      </p>
    );
  }

  const items = (data?.data?.items || []).filter(
    (item) =>
      item.type !== "conversation" ||
      item.conversation?.id !== props.conversation.id,
  );

  if (items.length === 0) return null;

  return (
    <Collapsible className="w-full bg-white" open={props.open}>
      <CollapsibleContent className="overflow-visible">
        <LinkedActivityRowList>
          {items.map((item) =>
            item.type === "conversation" ? (
              <ConversationLinkedActivityView
                key={item.id}
                conversation={item.conversation}
                conversationOrigin={item.conversation_origin}
              />
            ) : item.type === "jira_issue" ? (
              <JiraIssueView item={item} key={item.id} />
            ) : item.type === "slack_message" ? (
              <SlackMessageView item={item} key={item.id} />
            ) : item.type === "customer_complaint" &&
              item.customer_complaint ? (
              <LinkedActivityRow
                key={item.id}
                href={`/complaints/${item.customer_complaint.id}`}
                title={
                  item.customer_complaint.title ||
                  item.customer_complaint.summary
                }
                identifier="Complaint"
                date={
                  item.customer_complaint.created_at ||
                  item.entity_created_at ||
                  item.created_at
                }
              />
            ) : null,
          )}
        </LinkedActivityRowList>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ConversationLinkedActivityView(props: {
  conversation: ConversationThreadReadConversation | null;
  conversationOrigin?: string | null;
}) {
  const conversation = props.conversation;

  if (!conversation) return null;

  const isJira = conversation.data_source?.id === "jira";
  const ticketNumber =
    isJira && conversation.jira_issue_key
      ? conversation.jira_issue_key
      : conversation.external_id;
  const isDuplicate = props.conversationOrigin === "duplicate";

  return (
    <LinkedActivityRow
      href={conversation.external_url}
      leading={<CustomerImage customer={conversation.requester} />}
      title={conversation.subject}
      identifier={ticketNumber}
      badges={
        isDuplicate && (
          <Badge className="shrink-0 gap-1 rounded-sm bg-neutral-200 px-1.5 py-0.5 text-sm font-medium text-neutral-700">
            Duplicate
          </Badge>
        )
      }
      date={conversation.external_created_at || conversation.created_at}
    />
  );
}
